import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useMutation } from 'react-query';
import { addDays } from 'date-fns';
import { ImageType } from '@types';
import { API_DOMAIN } from '@constants';
import useToggle from '@hooks/useToggle';
import ImageComponent from '@components/Image';
import IconClose from '@components/icons/IconClose';
import axios from '@utils/axios';
import cn from '@utils/cn';

type IntroAdv = {
  id: number;
  pc_img: string;
  pc_url: string;
  /** 0: 只显示1次直到有更新, 1: 每天显示1次, 2: 每次访问都显示 */
  style: 0 | 1 | 2;
  /** 0: 曝光 , 1: 特定用户 */
  type: 0 | 1;
  uids?: string;
};

type StatPayload = {
  id: number;
  /** 0: 曝光, 1: 点击 */
  event: 0 | 1;
  type: IntroAdv['type'];
};

const getStorageKey = (id: number) => {
  return `720-editor-adv-${id}`;
};

function filterIntroAdvItems(items: any[]): IntroAdv[] {
  const keys = ['id', 'pc_img', 'pc_url', 'style', 'type', 'uids'];

  return items.map((item) => {
    const newItem: any = {};
    keys.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        newItem[key] = item[key];
      }
    });
    return newItem as IntroAdv;
  });
}

export function IntroModal() {
  const [advList, setAdvList] = useState<IntroAdv[]>([]);
  const isOpen = useToggle(false);

  const initAdvs = async () => {
    const origItems = await axios.get(`${API_DOMAIN}/member/my/tour/adv`);
    const advs: IntroAdv[] = [];
    const items = filterIntroAdvItems(origItems);
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.style === 0) {
        const key = getStorageKey(item.id);
        if (JSON.stringify(item) !== localStorage.getItem(key)) {
          advs.push(item);
        }
      } else if (item.style === 1) {
        const key = getStorageKey(item.id);
        const expireTime = Number(localStorage.getItem(key)) || 0;
        if (new Date().getTime() > expireTime) {
          advs.push(item);
        }
      } else if (item.style === 2) {
        advs.push(item);
      }
    }
    setAdvList(advs);
  };

  const stats = useMutation(['adv-stats'], async (payload: StatPayload) => {
    const url = `${API_DOMAIN}/stats/adv/edit`;
    return axios.post(url, payload);
  });

  useEffect(() => {
    initAdvs();
  }, []);

  useEffect(() => {
    const item = advList[0];
    if (item) {
      isOpen.on();
      stats.mutateAsync({ id: item.id, event: 0, type: item.type });
    }
  }, [advList]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const adv = advList[0];

  if (!isOpen.isOn || !adv) return null;

  const handleClose = () => {
    isOpen.off();
    if (adv.style === 0) {
      localStorage.setItem(getStorageKey(adv.id), JSON.stringify(adv));
    } else if (adv.style === 1) {
      const tomorrowStart = addDays(new Date(), 1);
      tomorrowStart.setHours(0, 0, 0, 0);
      localStorage.setItem(
        getStorageKey(adv.id),
        String(tomorrowStart.getTime())
      );
    }
    setAdvList((prev) => {
      return prev.slice(1);
    });
  };

  const modal = (
    <div
      style={{ zIndex: 11000 }}
      className="fixed inset-0 bg-black-black/70"
      onClick={handleClose}
    >
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform text-base"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={cn('h-full w-full', {
            'cursor-pointer': !!adv.pc_url,
          })}
          onClick={() => {
            stats.mutateAsync({ id: adv.id, event: 1, type: adv.type });
            if (adv.pc_url) {
              window.open(adv.pc_url);
            }
          }}
        >
          <AdvImage url={adv.pc_img} onClose={handleClose} />
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

function AdvImage({ url, onClose }: { url: string; onClose: () => void }) {
  const [dimensions, setDimensions] = useState({
    loaded: false,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    setDimensions({
      loaded: false,
      width: 0,
      height: 0,
    });
    const img = new Image();
    img.src = url;
    img.onload = () => {
      setDimensions({
        width: img.width / 2,
        height: img.height / 2,
        loaded: true,
      });
    };
  }, [url]);

  if (!dimensions.loaded) {
    return null;
  }

  return (
    <>
      <ImageComponent
        className="flex h-full w-full rounded"
        style={{
          width: dimensions.width,
          height: dimensions.height,
        }}
        src={url}
        type={ImageType.image}
      />
      <IconClose
        active
        className="absolute right-0 -top-30 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
    </>
  );
}
